import {
  useEffect,
  useState,
  useRef,
  useMemo,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { useOutletContext, useLocation } from "react-router-dom";
import { updateMessageSeen } from "@endpoints/job.service";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import PaperSendIcon from "@assets/svg/PaperSendIcon";
import useChat from "../../../custom-hooks/useChat";
import { MESSAGE_EVENTS } from "@custom-hooks/useChat";
import MessageBubble from "./MessageBubble";
import PaperClipIcon from "@assets/svg/PaperClipIcon";
import NewMessageDivider from "./NewMessageDivider";
import { ISelectedContact } from "../MessagesPage";
import {
  uploadChatMedia,
} from "@endpoints/images.service";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import { RootState } from "@store/reducers";
import { useSocket } from "context/SocketProvider";

interface ChatListProps {
  selectedContact:ISelectedContact
}

const ChatList = ({ selectedContact }: ChatListProps) => {
  const isInitialLoad = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const socketRef = useSocket();
  const socket = socketRef?.current;
  const {
    isMessageLoading,
    messages,
    sendMessage,
    appendLocalMessage,
    loadOlderMessages,
    isOlderMessageLoading,
    triggerTyping,
    isTyping,
    hasNextPage
  } = useChat({
    jobId:selectedContact?.jobId,
    receiverDetails: selectedContact?.participantDetails,
  });
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<
    {
      id: string;
      file: File;
      previewUrl?: string;
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);

  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCurrentlyTypingRef = useRef(false);

  const clearAttachments = () => {
    setAttachments((prev) => {
      prev.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      return [];
    });
  };

  useEffect(() => {
    return () => {
      attachments.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, [attachments]);

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const nextItems = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      file,
      previewUrl: file.type?.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined,
    }));

    setAttachments((prev) => [...prev, ...nextItems]);
    event.target.value = "";
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const item = prev.find((entry) => entry.id === id);
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
      return prev.filter((entry) => entry.id !== id);
    });
  };

  const uploadSelectedAttachments = async () => {
    if (!attachments.length) return [];

    const recipientUserId =
      selectedContact?.participantDetails?.userId ||
      selectedContact?.participantDetails?._id ||
      "";

    const formData = new FormData();
    attachments.forEach((item) => {
      formData.append("files", item.file);
    });

    formData.append("jobId", selectedContact?.jobId || "");
    formData.append("recipientId", recipientUserId);
    formData.append("senderId", user?._id || user?.userId || "");
    formData.append("chatId", selectedContact?._id || "");
    formData.append("message", message || "");

    const mediaUploadResponse = await uploadChatMedia(formData);
    const uploadInfo = mediaUploadResponse?.data;

    if (!uploadInfo?.message) {
      throw new Error("Failed to create chat media message");
    }

    socket?.emit(MESSAGE_EVENTS.REFRESH_MESSAGES, {
      jobId: selectedContact?.jobId || "",
      recipientId: recipientUserId,
    });

    return uploadInfo;
  };

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el || isOlderMessageLoading) return;
    const isAtTop = el.scrollTop === 0;
    if (isAtTop && hasNextPage) {
      loadOlderMessages();
    }
  };

  const scrollToBottom = (smooth = false) => {
    if (messagesContainerRef?.current) {
      messagesContainerRef?.current.scrollTo({
        top: messagesContainerRef?.current?.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    } else {
      if (!isCurrentlyTypingRef.current) {
        triggerTyping(true);
        isCurrentlyTypingRef.current = true;
      }

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        triggerTyping(false);
        isCurrentlyTypingRef.current = false;
      }, 2000);
    }
  };

  const handleSendClick = async () => {
    if (isLoading || (!message.trim() && attachments.length === 0) || !selectedContact) return;

    try {
      setIsLoading(true);
      if (attachments.length > 0) {
        const uploadResult = await uploadSelectedAttachments();
        appendLocalMessage(uploadResult.message);
        setMessage("");
        clearAttachments();
      } else {
        await sendMessage({
          message,
          setMessage,
        });
      }

      triggerTyping(false);
      isCurrentlyTypingRef.current = false;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    } catch (error: any) {
      enqueueSnackbar(error?.message || "Failed to send message", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  //   useEffect(() => {
  //     if (messages?.length > 0) {
  //       // markMessagesAsSeen();
  //       scrollToBottom();
  //     }
  //   }, [messages]);

  useEffect(() => {
    if (!messages?.length) return;

    const el = messagesContainerRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const isNearBottom = distanceFromBottom < 150;
    // 20 is fetch message limit
    if (isNearBottom || isInitialLoad?.current) {
      isInitialLoad.current = false;
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (selectedContact?.jobId) isInitialLoad.current = true;
  }, [selectedContact?.jobId]);

  const unseenMessageFrom = useMemo(
    () => messages?.find((item) => !item?.seen),
    [messages]
  );

  const markMessagesAsSeen = async () => {
    if (!messages[0]?.messageData?.length) return;

    const unseenMessageIds = messages[0].messageData
      .filter((msg) => !msg?.seen)
      .map((msg) => msg?._id || msg?.messageId);

    if (unseenMessageIds.length === 0) return;

    try {
      await updateMessageSeen({ messageIds: unseenMessageIds });
    } catch (error) {
      console.error("Error marking messages as seen:", error);
    }
  };


  // console.log('this is selected contact',selectedContact,isMessageLoading)

  if (!selectedContact || isMessageLoading) {
    return (
      <div className="flex justify-center mt-40">
        <LoadingAnimation color="black" />
      </div>
    );
  }


  return (
    <div className="flex flex-col h-full w-full relative px-5 pt-2 pb-8 min-h-0">
      <div className="bg-[#fafafa] rounded-md flex flex-col flex-1 min-h-0">
        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex flex-col gap-5 flex-grow overflow-y-auto no-scrollbar p-6 min-h-0 h-[69vh]"
        >
          {isOlderMessageLoading ? (
            <div className="flex items-center justify-center font-medium text-xs gap-3">
              Loading older messages <LoadingAnimation color="#2e2e2e" />
            </div>
          ) : null}
          {messages?.length ? (
            messages?.map((item) => {
              return (
                <div
                  key={(item?._id || item?.tempMessageId || item?.messageId || item?.chatId)+item?.createdAt}
                  className="flex flex-col gap-2"
                >
                  {/* {item?._id} {unseenMessageFrom?._id} */}
                  {/* {item?._id === unseenMessageFrom?._id ? (
                    <NewMessageDivider />
                  ) : null} */}
                  <MessageBubble item={item} receiverDetails={selectedContact?.participantDetails}/>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-center flex h-[67vh] items-center justify-center pb-10">
              No messages available.
            </p>
          )}
        </div>

        {/* Typing and message Input area */}
        <div className="flex flex-col px-1.5 pb-1.5 pt-0">
          <div className="h-6 flex items-center px-2">
            {isTyping && (
              <span className="text-xs text-gray-500 italic">
                Typing...
              </span>
            )}
          </div>
          {attachments.length ? (
            <div className="flex flex-wrap gap-2 px-2 pb-2">
              {attachments.map((item) => (
                <div
                  key={item.id}
                  className="relative flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-2 py-2 pr-8 max-w-[220px]"
                >
                  {item.previewUrl ? (
                    <img
                      src={item.previewUrl}
                      alt={item.file.name}
                      className="h-10 w-10 rounded object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-white border border-gray-200 flex items-center justify-center text-[10px] font-semibold text-light-black uppercase flex-shrink-0">
                      {item.file.name.split(".").pop()?.slice(0, 4)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[11px] font-medium text-charcoal truncate">
                      {item.file.name}
                    </p>
                    <p className="text-[10px] text-light-black">
                      {(item.file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAttachment(item.id)}
                    className="absolute right-1 top-1 text-[10px] font-bold text-light-black hover:text-charcoal"
                    aria-label={`Remove ${item.file.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="flex gap-4 rounded-md border p-1.5 bg-white">
            <textarea
              className="px-3 py-2 w-full bg-white rounded text-xs font-medium focus:border-gray-300 focus:outline-none"
              rows={1}
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
            ></textarea>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept="image/*,application/pdf,.pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.ppt,.pptx"
              onChange={handleAttachmentChange}
            />
            <button
              className="text-xs text-charcoal font-medium bg-white px-1 hover:bg-gray-200 flex items-center size-7 justify-center rounded-md disabled:cursor-not-allowed"
              onClick={handleSendClick}
              disabled={isLoading || (!message.trim() && attachments.length === 0) || !selectedContact}
            >
              {isLoading ? (
                <LoadingAnimation color="black" className={"w-4 h-4"} />
              ) : (
                <PaperSendIcon />
              )}
            </button>
            <button
              type="button"
              className="text-xs text-charcoal font-medium bg-white px-1 hover:bg-gray-200 flex items-center size-7 justify-center rounded-md"
              onClick={handleAttachmentClick}
            >
              <PaperClipIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatList;
